/*
 * Backend messages (response.data.message) in the UI language.
 *
 * The API answers in English only; every fixed backend message is an `api.*`
 * key in en.json with its German counterpart in de.json. A message is matched
 * by its English text, so anything the table does not know — the runtime-built
 * messages with names or IDs in them, or a message added to the backend later —
 * is shown unchanged.
 *
 * Reads the language straight from localStorage (as convertText does) and the
 * JSON files directly, so Library/Utils can use it without an import cycle.
 */
import En from "./en.json";
import De from "./de.json";

const apiKeyByEnglish = new Map<string, keyof typeof De>(
  Object.entries(En)
    .filter(([key]) => key.startsWith("api."))
    .map(([key, text]) => [String(text).trim(), key as keyof typeof De])
);

const isGerman = () =>
  (localStorage.getItem("language") || "").toLowerCase().startsWith("de");

/**
 * The backend message in the UI language; unknown messages come back as sent,
 * and a missing or non-text message (some responses carry an object) as "".
 */
export const translateApiMessage = (message: unknown): string => {
  if (typeof message !== "string") return "";
  if (!isGerman()) return message;
  const key = apiKeyByEnglish.get(message.trim());
  // eslint-disable-next-line security/detect-object-injection -- key comes from our own table
  return (key && De[key]) || message;
};

/**
 * The toast text for an API response body: its message (or DRF's `detail`)
 * translated, or `fallback` when there is no usable text — never an empty or
 * "[object Object]" toast.
 */
export const apiResponseMessage = (data: any, fallback: string): string => {
  const text = [data?.message, data?.detail].find(
    (value) => typeof value === "string" && value.trim()
  );
  return text ? translateApiMessage(text) : fallback;
};
